import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@rs-core/context/UserAuthContext';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { useToastMessageContext } from '@worklist-2/patientPortal/src/context/ToastMessageContext';
import { useBooleanFlagValue } from '@rs-core/hooks/useFlags';

import { useDrawersStore } from '@worklist-2/patientPortal/src/stores/drawers';
import { useShareDrawerStore } from '@worklist-2/patientPortal/src/stores/share-drawer';
import { useStudiesStore } from '@worklist-2/patientPortal/src/stores/studies';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Icon from '@mui/material/Icon';
import Circle from '@mui/icons-material/Circle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';

import ProgressAlert from '@worklist-2/patientPortal/src/views/StudiesV2/ProgressAlert';
import MobileBottomDrawer from '../MobileBottomDrawer/MobileDrawer/MobileBottomDrawer';

import sendAnalyticsEvent from '@worklist-2/patientPortal/src/analytics';
import getAddress from '@worklist-2/patientPortal/src/utils/getAddress';
import { isStudyEmpty } from '@worklist-2/patientPortal/src/views/StudiesV2/helpers';
import getPatientName from '@worklist-2/patientPortal/src/utils/getPatientName';
import { STUDY_STATUS, DATE_FORMATS } from '@worklist-2/patientPortal/src/consts';
import { STUDY_DOWNLOAD } from '@worklist-2/patientPortal/src/analytics/eventTypes';
import { STUDY_CARD_STYLES, formatStudyDate } from './StudyCardMobile.config';
import {
	BLUME_PRIMARY_COLOR,
	BLUME_SECONDARY_COLOR,
	BLUME_QUARTERARY_COLOR,
	BLUME_BRAND_PRIMARY_COLOR,
	BLUME_BORDER_SECONDARY_COLOR,
} from '../../consts/styles';

import userIcon from '@worklist-2/ui/src/assets/img/homepage/user.png';
import share from '@worklist-2/ui/src/assets/img/homepage/share.svg';
import downloadIcon from '@worklist-2/ui/src/assets/img/homepage/download.svg';
import StudyCardPhoneIcon from '@worklist-2/patientPortal/src/icons/StudyCardPhoneIcon';
import StudyCardAddressIcon from '@worklist-2/patientPortal/src/icons/StudyCardAddressIcon';
import StudyCardFacilityIcon from '@worklist-2/patientPortal/src/icons/StudyCardFacilityIcon';
import BlumeShareStudyContent from '../BlumeShareStudyContent/BlumeShareStudyContent';

const StudyCardMobile = ({ study }) => {
	const { t } = useTranslation('homepage');
	const { setToastMsg } = useToastMessageContext();
	const { patientMapping } = useAuth();

	const phoenixBlumeNewShareFlow = useBooleanFlagValue('phoenix-blume-new-share-flow');

	const initStudyDrawer = useShareDrawerStore(state => state.initStudyDrawer);

	const downloadStudy = useStudiesStore(state => state.downloadStudy);
	const isDownloading = useStudiesStore(useShallow(state => state.studyDownloadStatus?.[study?.id]?.loading));
	const progressMBs = useStudiesStore(useShallow(state => state.studyDownloadStatus?.[study?.id]?.progress));

	const setStudyInformationDetails = useDrawersStore(state => state.setStudyInformationDetails);

	const [patientName, setPatientName] = useState('');
	const [isShareOpen, setIsShareOpen] = useState(false);

	const studyDate = useMemo(() => formatStudyDate(study, DATE_FORMATS.MONTH_DAY_YEAR), [study]);

	const studyTime = useMemo(() => formatStudyDate(study, DATE_FORMATS.TIME_WITH_AM_PM), [study]);

	const address = useMemo(() => getAddress(study.imagingOrganization?.address), [study]);

	const phone = useMemo(() => {
		const { imagingOrganization } = study;

		if (!imagingOrganization) {
			return null;
		}

		return imagingOrganization.phone;
	}, [study]);

	const handleStudyDownload = useCallback(async () => {
		if (isDownloading) return null;

		await downloadStudy({ study, setToastMsg, t });

		sendAnalyticsEvent(STUDY_DOWNLOAD, { studyId: study.id, studyDescription: study.description });
	}, [downloadStudy, isDownloading, setToastMsg, study, t]);

	useEffect(() => {
		const { patientId } = study;
		setPatientName(getPatientName({ patientMapping, patientId }));
	}, []);

	const handleStudyNavigate = () => {
		setStudyInformationDetails({ study });
	};

	const handleClickShareStudy = () => {
		if (phoenixBlumeNewShareFlow) {
			setIsShareOpen(true);
			return;
		}

		initStudyDrawer({ study, forceFacilityFetch: true });
	};

	return (
		<Card sx={STUDY_CARD_STYLES.WRAPPER} variant="outlined">
			<Stack direction="row" justifyContent="space-between">
				<Stack flex={1} sx={{ whiteSpace: 'nowrap', gap: '8px', alignItems: 'center', flexDirection: 'row' }}>
					<Typography color={BLUME_PRIMARY_COLOR} fontSize={14} fontWeight={500}>
						{studyDate}
					</Typography>
					<Circle sx={{ fontSize: 6, color: 'rgba(102, 112, 133, 1)' }} />
					<Typography color={BLUME_SECONDARY_COLOR} fontSize={14} fontWeight={400}>
						{studyTime}
					</Typography>
				</Stack>
			</Stack>
			<Divider sx={{ borderColor: BLUME_BORDER_SECONDARY_COLOR }} />
			<Box sx={STUDY_CARD_STYLES.INFO_WRAPPER}>
				<Typography color={BLUME_PRIMARY_COLOR} fontSize={16} fontWeight={500}>
					{study.description}
				</Typography>
				<Typography sx={{ ...STUDY_CARD_STYLES.INFO_ITEM, fontWeight: 600 }}>
					<Avatar src={userIcon} sx={{ height: 24, width: 24 }} />
					{patientName}
				</Typography>
				<Box sx={STUDY_CARD_STYLES.INFO_ITEM}>
					<StudyCardFacilityIcon />
					<Typography color={BLUME_QUARTERARY_COLOR}>
						{study.imagingOrganization?.name || study.managingOrganization?.name || '-'}
					</Typography>
				</Box>
				{address && (
					<Box sx={{ ...STUDY_CARD_STYLES.INFO_ITEM, display: 'grid', gridTemplateColumns: '16px auto' }}>
						<StudyCardAddressIcon />

						<Box alignItems="center" display="flex">
							<Typography
								noWrap
								color={BLUME_BRAND_PRIMARY_COLOR}
								component="address"
								sx={{ maxWidth: '72dvw' }}
							>
								{address}
							</Typography>
							<IconButton
								component="a"
								href={`https://www.google.com/maps/place/${encodeURIComponent(address)}`}
								rel="noopener"
								sx={{ color: BLUME_BRAND_PRIMARY_COLOR, padding: 0 }}
								target="_blank"
							>
								<OpenInNewIcon sx={{ width: '16px', height: '16px' }} />
							</IconButton>
						</Box>
					</Box>
				)}

				{phone && (
					<Box sx={STUDY_CARD_STYLES.INFO_ITEM}>
						<StudyCardPhoneIcon />
						<Typography color={BLUME_BRAND_PRIMARY_COLOR}>{phone}</Typography>
					</Box>
				)}
			</Box>
			{study.status !== STUDY_STATUS.COMPLETED && (
				<ProgressAlert sx={{ marginTop: '12px', height: 'max-content' }} />
			)}
			<Box display="flex" flexDirection="column" gap="10px">
				<Button
					color="rsPrimary"
					sx={{
						...STUDY_CARD_STYLES.BUTTON,
						background: 'var(--color-primary)',
						color: 'white',
					}}
					variant="contained"
					onClick={handleStudyNavigate}
				>
					{t('View Results')}
				</Button>
				{!isStudyEmpty(study) ? (
					<Button color="info" sx={STUDY_CARD_STYLES.BUTTON} variant="outlined" onClick={handleStudyDownload}>
						{isDownloading ? (
							<Box sx={STUDY_CARD_STYLES.DOWNLOAD_PROGRESS}>
								<CircularProgress color="info" size={16} /> &#40;{progressMBs || 0} MB&#41;
							</Box>
						) : (
							<>
								<Icon component={downloadIcon} sx={{ height: '20px' }} />
								{t('Download Results')}
							</>
						)}
					</Button>
				) : null}
				<Button color="info" sx={STUDY_CARD_STYLES.BUTTON} variant="outlined" onClick={handleClickShareStudy}>
					<Icon component={share} sx={{ height: '20px' }} />
					{t('Share via email')}
				</Button>
			</Box>
			{phoenixBlumeNewShareFlow && (
				<MobileBottomDrawer background="#ffffff" open={isShareOpen} onClose={() => setIsShareOpen(false)}>
					<Box sx={STUDY_CARD_STYLES.SHARE_DRAWER_WRAPPER}>
						<Typography sx={STUDY_CARD_STYLES.SHARE_DRAWER_TITLE}>
							{t('Results are ready to share')}
						</Typography>
						<CloseIcon
							sx={STUDY_CARD_STYLES.SHARE_DRAWER_CLOSE_ICON}
							onClick={() => setIsShareOpen(false)}
						/>
					</Box>
					<BlumeShareStudyContent open={isShareOpen} setOpen={setIsShareOpen} study={study} />
				</MobileBottomDrawer>
			)}
		</Card>
	);
};

export default StudyCardMobile;
